import '../../../../Components/Form/form.css'
import '../search-traveller.css'

import { useState } from 'react'

import { Modal } from '../../../../Components'
import { FC } from '../../../../Services'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import { showToast } from '../../../../utils/toast'
import { getElementsByService } from '../Functions'
import { Icon } from '../Icons'
import { SectionShell } from '../SectionShell'
import { ClientCodeInput } from '../TransferField/ClientCodeInput'
import { useTransferField } from '../TransferField/Hook'
import { TravellerInput } from '../TransferField/TravellerInput'
import { TravellersCard } from '../TransferField/TravellersCard'
import { setErrors, validateInput } from './Functions'

const language = cap.language.state || 'IT'
const t = translations[language]

const {
  traveller: { name, lastname, email, clientCode, clientBusinessName }
} = getElementsByService()

export const SearchTravellerSection = ({
  state: [state, setState],
  travellerIsPresent: [, setTravellerIsPresent],
  onCancel,
  onTravellerChosen,
  readOnly,
  agenticLocked = false
}) => {
  const stateProp = [state, setState]
  const [ragioneSociale, setRagioneSociale] = useTransferField(
    [state, setState],
    clientBusinessName.location,
    stateProp.count
  )
  const [codiceCliente, setCodiceCliente] = useTransferField(
    [state, setState],
    clientCode.location,
    stateProp.count
  )
  const [nome, setNome] = useTransferField([state, setState], name.location, stateProp.count)
  const [cognome, setCognome] = useTransferField(
    [state, setState],
    lastname.location,
    stateProp.count
  )
  const [mail, setMail] = useTransferField([state, setState], email.location, stateProp.count)
  const [foundTravellers, setFoundTravellers] = useState([])
  const [modalVisible, setModalVisible] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isChoosing, setIsChoosing] = useState(false)
  const [nameError, setNameError] = useState(false)
  const [lastnameError, setLastnameError] = useState(false)
  const [emailError, setEmailError] = useState(false)

  const shouldDisplayTravellerInputs = ragioneSociale && codiceCliente
  const modalBusy = isSearching || isChoosing

  const searchTraveller = async () => {
    if (
      !validateInput(
        { nome, cognome, mail },
        { setNome, setCognome, setMail },
        { setNameError, setLastnameError, setEmailError }
      )
    )
      return

    setErrors(false, false, false, setNameError, setLastnameError, setEmailError)
    setFoundTravellers([])
    setModalVisible(true)
    setIsSearching(true)

    try {
      const listProfilesData = await FC.service('info').get('getAutocompleteProfile', {
        query: { profileData: { nome, cognome, mail }, codiceCliente }
      })
      setFoundTravellers(listProfilesData || [])
    } catch {
      showToast('error', t.errore, t.gfErroreRicercaViaggiatore)
      setModalVisible(false)
    } finally {
      setIsSearching(false)
    }
  }

  const chooseTraveller = async (pid) => {
    setIsChoosing(true)
    try {
      const res = await FC.service('info').get('getProfileInfo', { query: { pid } })
      if (res) {
        setState(
          {
            ...state,
            transferInfo: {
              ...state.transferInfo,
              infoPax: {
                ...state.transferInfo.infoPax,
                listOfPax: [
                  {
                    ...state.transferInfo.infoPax.listOfPax[0],
                    profileData: {
                      ...state.transferInfo.infoPax.listOfPax[0].profileData,
                      profiloViaggiatoreWS02: res
                    }
                  }
                ]
              }
            }
          },
          true
        )
        setModalVisible(false)
        onTravellerChosen?.()
        setTravellerIsPresent(true)
      } else {
        showToast('error', t.errore, t.gfErroreCaricamentoViaggiatore)
      }
    } catch {
      showToast('error', t.errore, t.gfErroreCaricamentoViaggiatore)
    } finally {
      setIsChoosing(false)
    }
  }

  const clearFields = () => {
    setCodiceCliente('')
    setRagioneSociale('')
    setNome('')
    setCognome('')
    setMail('')
    setErrors(false, false, false, setNameError, setLastnameError, setEmailError)
  }

  const closeModal = () => {
    if (modalBusy) return
    setModalVisible(false)
    setIsSearching(false)
    setIsChoosing(false)
  }

  const renderModalBody = () => {
    if (isSearching) {
      return (
        <div className="gf-traveller-picker__state" role="status" aria-live="polite">
          <span className="gf-traveller-picker__spinner" aria-hidden="true" />
          <p className="gf-traveller-picker__state-title">{t.gfRicercaInCorso}</p>
          <p className="gf-traveller-picker__state-text">{t.gfRicercaInCorsoDettaglio}</p>
        </div>
      )
    }

    if (isChoosing) {
      return (
        <div className="gf-traveller-picker__state" role="status" aria-live="polite">
          <span className="gf-traveller-picker__spinner" aria-hidden="true" />
          <p className="gf-traveller-picker__state-title">{t.gfCaricamentoProfilo}</p>
          <p className="gf-traveller-picker__state-text">{t.gfCaricamentoProfiloDettaglio}</p>
        </div>
      )
    }

    if (foundTravellers.length) {
      return (
        <>
          <p className="gf-traveller-picker__count">
            {foundTravellers.length}{' '}
            {foundTravellers.length === 1 ? t.gfRisultatoTrovato : t.gfRisultatiTrovati}
          </p>
          <div className="gf-traveller-picker__results">
            {foundTravellers.map((user, index) => (
              <TravellersCard
                key={user.pid || index}
                nome={user.nome}
                cognome={user.cognome}
                email={user.email}
                sex={user.sesso}
                onClick={() => chooseTraveller(user.pid)}
              />
            ))}
          </div>
        </>
      )
    }

    return (
      <div className="gf-traveller-picker__state gf-traveller-picker__state--empty">
        <span className="gf-traveller-picker__empty-icon" aria-hidden="true">
          <Icon name="user" size={22} />
        </span>
        <p className="gf-traveller-picker__state-title">{t.gfNessunViaggiatoreTrovato}</p>
        <p className="gf-traveller-picker__state-text">{t.gfNessunViaggiatoreTrovatoHint}</p>
      </div>
    )
  }

  return (
    <SectionShell
      id="search-traveller"
      icon="user"
      title={t.gfRicercaViaggiatore}
      storageKey="gigiForm.section.search-traveller"
      contentId="search-traveller-content"
      defaultCollapsed={false}
      headerExtra={
        onCancel ? (
          <button type="button" className="gf-btn gf-btn--ghost" onClick={onCancel}>
            <Icon name="arrow_left" size={13} />
            <span className="gf-lbl">{t.annulla}</span>
          </button>
        ) : null
      }
    >
      <div className="gf-search-traveller">
        <Modal
          visible={modalVisible}
          header={t.gfSelezionaViaggiatore}
          className="gf-dialog-modal gf-traveller-picker-modal"
          onHide={closeModal}
          noDismissable={modalBusy}
          closable={!modalBusy}
          style={{ width: 'min(720px, 92vw)' }}
          contentStyle={{ padding: 0 }}
          footer={
            <div className="gf-dialog-modal__footer">
              <button type="button" className="gf-btn" onClick={closeModal} disabled={modalBusy}>
                {t.annulla}
              </button>
            </div>
          }
        >
          <div className="gf-traveller-picker__body">{renderModalBody()}</div>
        </Modal>

        <div className="ff-grid">
          <ClientCodeInput
            variant="v2"
            state={stateProp}
            valueCode={[codiceCliente, setCodiceCliente]}
            valueRS={[ragioneSociale, setRagioneSociale]}
            itemCode={clientCode}
            itemRS={clientBusinessName}
            readOnly={readOnly || agenticLocked}
          />
        </div>

        {shouldDisplayTravellerInputs && (
          <>
            <div className="ff-grid gf-search-traveller__fields">
              <TravellerInput
                variant="v2"
                state={stateProp}
                value={[nome, setNome]}
                item={name}
                readOnly={readOnly || agenticLocked}
                miss={[nameError, setNameError]}
              />
              <TravellerInput
                variant="v2"
                state={stateProp}
                value={[cognome, setCognome]}
                item={lastname}
                readOnly={readOnly || agenticLocked}
                miss={[lastnameError, setLastnameError]}
              />
              <TravellerInput
                variant="v2"
                state={stateProp}
                value={[mail, setMail]}
                item={email}
                readOnly={readOnly || agenticLocked}
                miss={[emailError, setEmailError]}
              />
            </div>
            <p className="gf-search-traveller__hint">{t.gfHintRicercaViaggiatore}</p>
            <div className="gf-search-traveller__actions">
              <button
                type="button"
                className="gf-btn gf-btn--primary"
                onClick={() => searchTraveller()}
                disabled={isSearching || agenticLocked}
              >
                {isSearching ? (
                  <span className="gf-btn__spinner" aria-hidden="true" />
                ) : (
                  <Icon name="eye" size={14} />
                )}
                {isSearching ? t.gfRicercaPuntini : t.gfCerca}
              </button>
              <button
                type="button"
                className="gf-btn"
                onClick={() => clearFields()}
                disabled={isSearching || agenticLocked}
              >
                {t.gfPulisciCampi}
              </button>
            </div>
          </>
        )}
      </div>
    </SectionShell>
  )
}
